<script setup>
import { NuxtLink } from "#components";

const props = defineProps({
    isLink: {
        type: Boolean,
        default: true,
    },
    variant: {
        type: String,
        required: false,
        validator(value) {
            return ["primary", "secondary", "white"].includes(value);
        },
    },
    isExternal: {
        type: Boolean,
        default: false,
    },
    hasIcon: {
        type: Boolean,
        default: true,
    },
    isDark: {
        type: Boolean,
        default: false,
    },
    svgClass: {
        type: String,
        default: "",
    },
    ariaLabel: {
        type: String,
        default: "",
    },
});
</script>

<template>
    <component
        :is="isLink ? NuxtLink : 'button'"
        :target="isExternal ? '_blank' : '_self'"
        class="btn select-none"
        :aria-label="ariaLabel"
        :class="[
            {
                'bg-primary-orange': variant === 'primary',
                'bg-base-doveGrey': variant === 'secondary',
                'bg-base-white': variant === 'white',
                'lg:!h-[3.7vw] 3xl:!h-[71.1px]': $slots.default && hasIcon,
                'lg:!h-[3.17vw] 3xl:!h-[61px]': !$slots.default && hasIcon,
            },
        ]"
    >
        <span class="_svg" v-if="$slots.svg">
            <slot name="svg" />
        </span>

        <p
            class="slt-button"
            :class="[
                {
                    hidden: !$slots.default,
                    'pr-[0.6vw] xl:pr-2': $slots.svg && hasIcon,
                    'pl-2': !$slots.svg && hasIcon,
                },
            ]"
        >
            <slot />
        </p>

        <div
            v-if="hasIcon && (!$slots.svg || !$slots.default)"
            class="btn__arrow__group"
        >
            <SvgArrowOblique16
                v-if="isExternal"
                :isDark="isDark"
                :class="svgClass"
                class="btn__arrow btn__arrow__external--main"
                :stroke-width="1.6"
            />
            <SvgArrowOblique16
                v-if="isExternal"
                :isDark="isDark"
                :class="svgClass"
                class="btn__arrow btn__arrow__external--hover"
                :stroke-width="1.67"
            />
            <SvgArrowForward24
                v-if="!isExternal"
                :isDark="isDark"
                :class="svgClass"
                class="btn__arrow btn__arrow--main"
                :stroke-width="2.4"
            />
            <SvgArrowForward24
                v-if="!isExternal"
                :isDark="isDark"
                :class="svgClass"
                class="btn__arrow btn__arrow--hover"
            />
        </div>
    </component>
</template>

<style scoped>
.btn {
    @apply flex h-[12.21vw] items-center justify-center gap-2 rounded-full px-5 font-bold transition-transform duration-300 ease-in-out [will-change:transform] xs:h-[48px] sm:hover:scale-[1.03] lg:gap-[0.49vw] xl:gap-[0.4vw] 3xl:gap-[8px];
}
._svg {
    @apply flex size-[6.1vw] items-center justify-center overflow-hidden xs:size-[24px];
}

.btn__arrow__group {
    @apply relative inline-block size-[6.1vw] overflow-hidden align-middle xs:size-[24px];
}
.btn__arrow {
    @apply absolute right-0 top-0;
    transition:
        transform 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33),
        opacity 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33);
}
.btn__arrow--hover {
    transform: translate3d(-100%, 0, 0);
    opacity: 0;
}
.btn__arrow__external--hover {
    transform: translate3d(-100%, 100%, 0);
    opacity: 0;
}

@media (min-width: 640px) {
    .btn:hover .btn__arrow--main {
        transform: translate3d(100%, 0, 0);
        opacity: 0;
    }
    .btn:hover .btn__arrow--hover {
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
    .btn:hover .btn__arrow__external--main {
        transform: translate3d(100%, -100%, 0);
        opacity: 0;
    }
    .btn:hover .btn__arrow__external--hover {
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
}
</style>
